import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  updateSubscription,
  updateSubscriptionReview,
} from '../../store/actions/payments';
import { Button, InputNumber, Card, Typography } from 'antd';
import { ModalComponent } from 'src/components';
import { useAuth } from '@frontegg/react';
import { debounce } from 'lodash';
import { setSubscriptionUpdateReview } from '../../store/slices/payments';
import { analytics } from 'src/services';

const { Title, Text } = Typography;

const UpdateSubscription = ({ subscription, onSuccess }) => {
  const [showSeatsModal, setShowSeatsModal] = useState(false);
  const [newQuantity, setNewQuantity] = useState(0);
  const [isUpdating, setIsUpdating] = useState(false);
  const { user } = useAuth();
  const dispatch = useDispatch();
  const { subcriptionUpdateReviewLoading, subcriptionUpdateReview } =
    useSelector((state) => state.paymentsState);

  useEffect(() => {
    if (subscription?.quantity) {
      setNewQuantity(subscription.quantity);
    }
  }, [subscription]);

  const debouncedReviewUpdate = useCallback(
    debounce((quantity) => {
      dispatch(updateSubscriptionReview({ quantity }));
    }, 1000),
    [dispatch]
  );

  // Update the handleQuantityChange function to use debouncing
  const handleQuantityChange = (value) => {
    setNewQuantity(value);
    debouncedReviewUpdate(value);
  };

  const resetSubscriptionUpdateReview = () => {
    dispatch(setSubscriptionUpdateReview(null));
  };

  const handleUpdateSeats = () => {
    if (newQuantity === subscription?.quantity) {
      return;
    }
    analytics.track('Clicked Submit Update Seats In Billing', {
      email: user?.email,
      quantity: newQuantity,
    });
    setIsUpdating(true);
    dispatch(
      updateSubscription({ quantity: newQuantity }, () => {
        analytics.track('Successfully Updated Seats In Billing', {
          email: user?.email,
          quantity: newQuantity,
        });
        setShowSeatsModal(false);
        setIsUpdating(false);
      })
    );
  };

  const handleCancel = () => {
    setNewQuantity(subscription?.quantity || 0);
    resetSubscriptionUpdateReview();
    setShowSeatsModal(false);
  };

  const onClick = () => {
    analytics.track('Clicked Update Seats In Billing', { email: user?.email });
    setShowSeatsModal(true);
  };

  return (
    <>
      <Button
        type="primary"
        size="small"
        onClick={onClick}
        className="bg-[#13C17F] hover:bg-[#0F9F67]"
      >
        Update
      </Button>

      {/* Seats Update Modal */}
      <ModalComponent
        title="Update subscription"
        open={showSeatsModal}
        handleCancel={handleCancel}
        centered={true}
        submitButtonText=""
        cancelButtonText=""
        additionalBody={
          <>
            <div className="flex flex-col gap-6">
              <div>
                <Title level={4}>Update subscription</Title>
                <div className="my-4">
                  <div className="flex items-center justify-between">
                    <Text strong>New seat count</Text>
                    <InputNumber
                      min={1}
                      value={newQuantity}
                      onChange={handleQuantityChange}
                      className="w-1/2"
                      controls={true}
                    />
                  </div>
                </div>
              </div>

              <Card title="Order Summary" className="bg-white">
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <Text>Current seats</Text>
                    <Text strong>{subscription?.quantity || 0}</Text>
                  </div>
                  <div className="flex justify-between">
                    <Text>New seats</Text>
                    <Text strong>{newQuantity}</Text>
                  </div>

                  <div className="flex justify-between">
                    <Text>Amount Due</Text>
                    <Text strong>
                      ${subcriptionUpdateReview?.total || 'N/A'}
                    </Text>
                  </div>
                </div>
              </Card>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <Button onClick={handleCancel}>Cancel</Button>
              <Button
                type="primary"
                onClick={handleUpdateSeats}
                loading={isUpdating || subcriptionUpdateReviewLoading}
                disabled={newQuantity === subscription?.quantity}
                className="bg-[#13C17F] hover:bg-[#0F9F67]"
              >
                {isUpdating
                  ? 'Updating...'
                  : subcriptionUpdateReviewLoading
                    ? 'Previewing invoice...'
                    : 'Update'}
              </Button>
            </div>
          </>
        }
      />
    </>
  );
};

export default UpdateSubscription;
